import React, { useContext, useRef, useState } from 'react'
import type { RouteComponentProps } from '@reach/router'
import { ApiFetch, ApiPut } from '../../Common/Api'
import PageHeading from '../../Common/PageHeading'
import UserContext, { Roles } from '../../../UserContext'
import type { ProviderPreferenceAudit, ProviderPreferenceDetail, ProviderPreferenceLookupResult, SpecialtyCategoryOverrideCommand } from '../../../models/AdminProviderPreference'

const ProviderPreferenceOverrides: React.FC<RouteComponentProps> = () => {
    const { hasAnyRole } = useContext(UserContext)
    const [providerUserID, setProviderUserID] = useState('')
    const [email, setEmail] = useState('')
    const [provider, setProvider] = useState<ProviderPreferenceDetail>()
    const [history, setHistory] = useState<ProviderPreferenceAudit[]>([])
    const [reason, setReason] = useState('')
    const [reasonError, setReasonError] = useState('')
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')
    const [savingCategory, setSavingCategory] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [isSavingOverride, setIsSavingOverride] = useState(false)
    const searchRequestID = useRef(0)
    const overrideInFlight = useRef(false)
    const reasonInput = useRef<HTMLTextAreaElement>(null)

    if (!hasAnyRole(Roles.ProviderPreferenceAdministrator))
        return <div className='error-text'>You are not authorized to manage provider preferences.</div>

    const loadProvider = (providerID: number, requestID: number) => Promise.all([
        ApiFetch<ProviderPreferenceDetail>(`AdminProviderPreferences/${providerID}`),
        ApiFetch<ProviderPreferenceAudit[]>(`AdminProviderPreferences/${providerID}/specialty-category-overrides`)
    ]).then(([detail, audits]) => {
        if (requestID !== searchRequestID.current) return
        setProvider(detail)
        setHistory(audits)
    })

    const search = () => {
        if (isSavingOverride) return

        const trimmedProviderUserID = providerUserID.trim()
        const hasID = trimmedProviderUserID !== ''
        const hasEmail = email.trim() !== ''
        const requestID = searchRequestID.current + 1
        searchRequestID.current = requestID
        setProvider(undefined)
        setHistory([])
        setReason('')
        setReasonError('')
        setError('')
        setMessage('')
        if (hasID && !/^[1-9]\d*$/.test(trimmedProviderUserID)) {
            setError('Enter a positive whole-number provider user ID.')
            return
        }
        if (hasID === hasEmail) {
            setError('Enter exactly one provider user ID or email address.')
            return
        }

        const query = hasID ? { providerUserID: Number(trimmedProviderUserID) } : { email: email.trim() }
        setIsSearching(true)
        ApiFetch<ProviderPreferenceLookupResult[]>('AdminProviderPreferences/providers', query)
            .then(results => {
                if (requestID !== searchRequestID.current) return
                if (results.length !== 1) {
                    setError('No provider was found.')
                    return
                }
                return loadProvider(results[0].providerUserID, requestID)
            })
            .catch(() => {
                if (requestID === searchRequestID.current)
                    setError('Unable to find the provider.')
            })
            .finally(() => {
                if (requestID === searchRequestID.current)
                    setIsSearching(false)
            })
    }

    const applyOverride = (category: string, isAllowed: boolean) => {
        if (overrideInFlight.current) return

        if (!provider || !reason.trim()) {
            const message = 'Provide a reason before allowing or denying a category.'
            setReasonError(message)
            reasonInput.current?.focus()
            return
        }

        const command: SpecialtyCategoryOverrideCommand = { category, isAllowed, reason: reason.trim() }
        setError('')
        setReasonError('')
        setMessage('')
        setSavingCategory(category)
        setIsSavingOverride(true)
        overrideInFlight.current = true
        ApiPut<ProviderPreferenceDetail>(`AdminProviderPreferences/${provider.providerUserID}/specialty-category`, undefined, command)
            .then(detail => {
                setProvider(detail)
                setReason('')
                setMessage(`${category} was ${isAllowed ? 'allowed' : 'denied'}.`)
                return ApiFetch<ProviderPreferenceAudit[]>(`AdminProviderPreferences/${provider.providerUserID}/specialty-category-overrides`)
                    .then(setHistory)
                    .catch(() => setError('The category was updated, but override history could not be refreshed.'))
            })
            .catch((response: Error) => setError(response.message || 'Unable to update the provider specialty category.'))
            .finally(() => {
                setSavingCategory('')
                setIsSavingOverride(false)
                overrideInFlight.current = false
            })
    }

    return <>
        <PageHeading pageTitle='Provider Preference Overrides' createButton={false} filterButton={false} />
        <section className='preferences'>
            <label htmlFor='provider-user-id'>Provider User ID</label>
            <input id='provider-user-id' value={providerUserID} onChange={event => setProviderUserID(event.target.value)} disabled={isSavingOverride} />
            <label htmlFor='provider-email'>Provider Email</label>
            <input id='provider-email' type='email' value={email} onChange={event => setEmail(event.target.value)} disabled={isSavingOverride} />
            <button type='button' className='button' onClick={search} disabled={isSearching || isSavingOverride}>{isSearching ? 'Finding Provider' : 'Find Provider'}</button>
            {error && <div className='error-text' role='alert'>{error}</div>}
            {message && <div className='success-text' role='status'>{message}</div>}
            {provider && <>
                <div className='card provider-override-provider-card'>
                    <h4>{provider.providerName}</h4>
                    <p>{provider.email}</p>
                </div>
                <div className='card service-types-card provider-override-categories'>
                    <h6 className='margin-none color-black preferences-section-title'>
                        <span className='far fa-suitcase margin-right'></span>
                        <b>Service Categories</b>
                    </h6>
                    <p className='font-small color-black'>Use Allow or Deny to update a provider's selected categories after contract review.</p>
                    <label htmlFor='override-reason'>Reason for this change</label>
                    <textarea
                        id='override-reason'
                        ref={reasonInput}
                        value={reason}
                        onChange={event => {
                            setReason(event.target.value)
                            if (reasonError) setReasonError('')
                        }}
                        aria-invalid={reasonError ? true : undefined}
                        aria-describedby={reasonError ? 'override-reason-error' : undefined}
                        disabled={isSavingOverride}
                    />
                    {reasonError && <div id='override-reason-error' className='error-text' role='alert'>{reasonError}</div>}
                    <div className='service-types-list provider-override-category-list'>
                        {provider.availableSpecialtyCategories.map(value => {
                            const selected = provider.specialtyCategories.some(category => category.toLowerCase() === value.toLowerCase())
                            const allowed = provider.allowedSpecialtyCategories.some(category => category.toLowerCase() === value.toLowerCase())
                            const saving = savingCategory === value
                            const inputID = `override-category-${value.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`

                            return <div className='provider-override-category-row' key={value}>
                                <label className='custom-checkbox-label' htmlFor={inputID}>
                                    <input
                                        id={inputID}
                                        type='checkbox'
                                        aria-label={value}
                                        checked={selected}
                                        onChange={() => undefined}
                                        className='custom-checkbox-input'
                                        disabled={true}
                                    />
                                    <span className='custom-checkbox'></span>
                                    <span>{value}</span>
                                    {allowed && <span className='provider-override-standard-label'>Allowed</span>}
                                </label>
                                <div className='provider-override-actions'>
                                    <button type='button' className='button tiny provider-override-allow' aria-label={`Allow ${value}`} onClick={() => applyOverride(value, true)} disabled={allowed || saving || isSavingOverride}>Allow</button>
                                    <button type='button' className='button tiny provider-override-deny' aria-label={`Deny ${value}`} onClick={() => applyOverride(value, false)} disabled={!allowed || saving || isSavingOverride}>Deny</button>
                                </div>
                            </div>
                        })}
                    </div>
                </div>
                <div className='card provider-override-history'>
                    <h5>Override History</h5>
                    {history.length === 0 ? <p>No overrides recorded.</p> : <ul>
                        {history.map(audit => <li key={audit.serviceRequestAuditID}>{audit.changeDescription}</li>)}
                    </ul>}
                </div>
            </>}
        </section>
    </>
}

export default ProviderPreferenceOverrides